import { Component, Injector } from '@angular/core';
import { EndPoint } from '@tendercuts/providers';
import { UserState } from '@store/user';
import { ToastController, LoadingController, ModalController, AlertController, PopoverController } from '@ionic/angular';
import { take, tap } from 'rxjs/operators';
import { AlertOptions, LoadingOptions, ToastOptions, ModalOptions, PopoverOptions } from '@ionic/core';
import { AlertPopoverPage } from './alert-popover/alert-popover';

@Component({
  selector: 'app-base',
  templateUrl: 'base.html'
})
export class BasePage {
  public loader: HTMLIonLoadingElement;
  public toast: any;
  private popover: HTMLIonPopoverElement;

  protected toastCtrl: ToastController;
  protected loadingCtrl: LoadingController;
  protected modalCtrl: ModalController;
  protected endPoint: EndPoint;
  protected alertCtrl: AlertController;
  protected popoverCtrl: PopoverController;

  constructor(protected injector: Injector) {
    this.loadingCtrl = injector.get(LoadingController);
    this.toastCtrl = injector.get(ToastController);
    this.modalCtrl = injector.get(ModalController);
    this.alertCtrl = injector.get(AlertController);
    this.popoverCtrl = injector.get(PopoverController);
    this.endPoint = injector.get(EndPoint);
  }

  get cdn() {
    return this.endPoint.cdn;
  }

  async presentLoader(message: string) {
    const loadingCtrl: LoadingOptions = {
      message
    };
    this.loader = await this.loadingCtrl.create(loadingCtrl);
    this.loader.present();
    return this.loader;
  }

  /**
   * Updates the loader text
   */
  updateLoader(message: string) {
    this.loader.message = message;
  }

  async dismissLoader() {
    let res: boolean;
    if (this.loader) {
      res = await this.loader.dismiss();
      this.loader = undefined;
    }

    return res;
  }

  showToast(message: string, duration: number = 4000) {
    this.endPoint.store
      .select(state => state.userState)
      .pipe(take(1))
      .subscribe(
        (async (userState: UserState) => {
          const toastOptions: ToastOptions =  {
            message,
            duration
          };
          const toast = await this.toastCtrl.create(toastOptions);
          toast.present();
        }
      ));
  }

  async showAlert(title: string, subTitle: string) {
    const alertOptions: AlertOptions = {
      header: title,
      subHeader: subTitle,
      buttons: ['OK']
    };
    const alert = await this.alertCtrl.create(alertOptions);
    alert.present();
    return alert;
  }

  /**
   * 
   * @param title 
   * @param subTitle 
   * @param message 
   * @param inputs 
   * @param buttons 
   * @param enableBackdropDismiss 
   */
  async showConfirm(
    title: string,
    subTitle: string,
    message: string,
    // show the input values what expected
    inputs: any [],
    buttons: any[],
    enableBackdropDismiss = true
  ) {

    const alertOptions: AlertOptions = {
      header: title,
      subHeader: subTitle,
      message: message,
      inputs: inputs,
      buttons,
      backdropDismiss: enableBackdropDismiss
    };
    const confirm = await this.alertCtrl.create(alertOptions);
    confirm.present();
    return confirm;
  }

  async present(page: any, params: any, callback?: any) {
    const modalOptions: ModalOptions = {
      component: page,
      componentProps: params
    };
    const profileModal = await this.modalCtrl.create(modalOptions);
    profileModal.onDidDismiss().then(callback);
    profileModal.present();
    return profileModal;
  }

  /**
   * A convenience method to show something went wrong!
   */
  somethingWentWrong(err?: any) {
    this.endPoint.store
      .select(state => state.userState)
      .pipe(
        take(1)
      ).subscribe((userState: UserState) => {
        this.dismissLoader();
        this.presentPopoverAlert(
          'Oops!',
          'Sorry, Something Went Wrong. Contact Tech Support',
          'ios-bug-outline',
          'alert-popover'
        );
      });
  }

  async presentPopoverAlert(
    title: string,
    content: string,
    iconName?: string,
    iconColor?: string,
    imagePath?: string
  ) {
    const popoverOpts: PopoverOptions = {
      component: AlertPopoverPage,
      componentProps: {
        iconName,
        title,
        content,
        iconColor,
        imagePath
      },
      cssClass: 'alert-popover'
    };
    this.popover = await this.popoverCtrl.create(popoverOpts);
    this.popover.present();
    return this.popover;
  }

  closePopover() {
    if (this.popover) {
      this.popover.dismiss();
      this.popover = undefined;
    }
  }

  internetPopoverAlert() {
    this.showConfirm(
      'No Internet',
      'Network not connected please check  your internet connection',
      "",
      [],
      [],
      false
    );
  }

  get userState() {
    return this.endPoint.store.select(state => state.userState);
  }
}
